/**
 * Gift Card Form Manager
 * Handles gift card product form validation, custom amount selection, and cart integration
 */

import { formatPrice, getElement, getAllElements, dispatchCartUpdatedEvent } from '../core/utils.js';
import { getStorage, setStorage } from '../core/storage.js';
import { t } from '../core/i18n.js';

export class GiftCardFormManager {
    constructor() {
        this.isGiftCardProduct = window.isGiftCardProduct || false;
        this.giftCardConfig = window.giftCardConfig || {
            minAmount: 100,
            maxAmount: 10000,
            suggestedAmounts: [500, 1000, 2000, 5000]
        };
        
        // When false, recipient name/email/message fields are hidden and not required
        // Defaults to true for backward compatibility (standard gift card behavior)
        this.requireRecipientFields = this.giftCardConfig.requireRecipientFields !== false;
        
        this.selectedAmount = null;
        this.selectedVariant = null; // Currently selected variant (variant-based gift cards only)
        this.formData = {
            recipientName: '',
            recipientEmail: '',
            giftMessage: '',
            senderName: ''
        };
        
        this.elements = {};
        this.isValid = false;
        this.hasAttemptedSubmit = false; // Track if user has tried to submit
        
        if (this.isGiftCardProduct) {
            this.init();
        }
    }

    /**
     * Initialize the gift card form manager
     */
    init() {
        try {
            this.bindElements();
            this.setupEventListeners();
            
            // Check if product has variants - if so, wait for variant selection
            if (this.giftCardConfig.hasVariants) {
                // Product has variants - listen for variant selection and use variant price
                console.log('GiftCardFormManager: Product has variants, waiting for variant selection');
                this.setupVariantPriceListener();
            } else if (!this.elements.customAmount && this.giftCardConfig.fixedAmount) {
                // Fixed amount template (no variants, no custom amount) - auto-set amount from config
                this.selectedAmount = this.giftCardConfig.fixedAmount;
                console.log('GiftCardFormManager: Fixed amount template detected, amount set to:', this.selectedAmount);
            } else {
                // Custom amount template - setup input handlers
                this.setupCustomAmountInput();
                this.setupQuickSelectButtons();
            }
            
            this.setupPhysicalCardOption();
            this.applyRecipientFieldsVisibility(); // Hide fields if requireRecipientFields === false
            this.updateButtonState();
            
            const templateType = this.giftCardConfig.hasVariants ? 'variants' : 
                                (this.giftCardConfig.allowCustomAmount === false ? 'fixed amount' : 'custom amount');
            console.log(`GiftCardFormManager initialized successfully (${templateType}, requireRecipientFields=${this.requireRecipientFields})`);
        } catch (error) {
            console.error('Failed to initialize GiftCardFormManager:', error);
        }
    }

    /**
     * Bind DOM elements
     */
    bindElements() {
        // Form elements
        this.elements.recipientName = getElement('.js-recipient-name');
        this.elements.recipientEmail = getElement('.js-recipient-email');
        this.elements.giftMessage = getElement('.js-gift-message');
        this.elements.senderName = getElement('.js-sender-name');
        
        // Custom amount elements
        this.elements.customAmount = getElement('.js-custom-amount');
        this.elements.selectedAmountDisplay = getElement('.js-selected-amount-display');
        this.elements.errorCustomAmount = getElement('.js-error-custom-amount');
        
        // Gift card type elements (new radiobutton structure)
        this.elements.giftCardTypeGroup = getElement('.js-gift-card-type-group');
        this.elements.giftCardTypeDigital = getElement('.js-gift-card-type-digital');
        this.elements.giftCardTypePhysical = getElement('.js-gift-card-type-physical');
        this.elements.physicalCardPriceDisplay = getElement('.js-physical-card-price-display');
        
        // Email section (to show/hide based on card type)
        this.elements.recipientEmailGroup = getElement('.js-recipient-email-group');
        
        // Physical delivery section
        this.elements.physicalDeliverySection = getElement('.js-physical-delivery-section');
        this.elements.deliveryName = getElement('.js-delivery-name');
        this.elements.deliveryLastname = getElement('.js-delivery-lastname');
        this.elements.deliveryAddress = getElement('.js-delivery-address');
        this.elements.deliveryAddress2 = getElement('.js-delivery-address2');
        this.elements.deliveryZip = getElement('.js-delivery-zip');
        this.elements.deliveryCity = getElement('.js-delivery-city');
        // this.elements.deliveryCountry = getElement('.js-delivery-country');
        
        // Display elements
        this.elements.summaryRecipient = getElement('.js-summary-recipient');
        this.elements.summarySender = getElement('.js-summary-sender');
        this.elements.charCount = getElement('.js-char-count');
        
        // Button and validation
        this.elements.addToCartButton = getElement('.js-add-to-cart-giftcard');
        this.elements.validationSummary = getElement('.js-validation-summary');
        
        // Error message elements
        this.elements.errorRecipientName = getElement('.js-error-recipient-name');
        this.elements.errorRecipientEmail = getElement('.js-error-recipient-email');
        this.elements.errorSenderName = getElement('.js-error-sender-name');
        this.elements.errorDeliveryName = getElement('.js-error-delivery-name');
        this.elements.errorDeliveryLastname = getElement('.js-error-delivery-lastname');
        this.elements.errorDeliveryAddress = getElement('.js-error-delivery-address');
        this.elements.errorDeliveryZip = getElement('.js-error-delivery-zip');
        this.elements.errorDeliveryCity = getElement('.js-error-delivery-city');
    }

    /**
     * Setup event listeners
     */
    setupEventListeners() {
        // Form field listeners
        if (this.elements.recipientName) {
            this.elements.recipientName.addEventListener('input', () => {
                this.elements.recipientName.dataset.touched = 'true';
                this.handleFormChange();
            });
            this.elements.recipientName.addEventListener('blur', () => {
                this.elements.recipientName.dataset.touched = 'true';
                this.validateField('recipientName');
            });
        }

        if (this.elements.recipientEmail) {
            this.elements.recipientEmail.addEventListener('input', () => {
                this.elements.recipientEmail.dataset.touched = 'true';
                this.handleFormChange();
            });
            this.elements.recipientEmail.addEventListener('blur', () => {
                this.elements.recipientEmail.dataset.touched = 'true';
                this.validateField('recipientEmail');
            });
        }

        if (this.elements.giftMessage) {
            this.elements.giftMessage.addEventListener('input', () => {
                this.updateCharCount();
                this.handleFormChange();
            });
        }

        if (this.elements.senderName) {
            this.elements.senderName.addEventListener('input', () => {
                this.elements.senderName.dataset.touched = 'true';
                this.handleFormChange();
            });
            this.elements.senderName.addEventListener('blur', () => {
                this.elements.senderName.dataset.touched = 'true';
                this.validateField('senderName');
            });
        }

        // Gift card type radio button listeners
        if (this.elements.giftCardTypeDigital) {
            this.elements.giftCardTypeDigital.addEventListener('change', () => {
                this.handleGiftCardTypeChange();
            });
        }
        
        if (this.elements.giftCardTypePhysical) {
            this.elements.giftCardTypePhysical.addEventListener('change', () => {
                this.handleGiftCardTypeChange();
            });
        }

        // Delivery address field listeners
        const deliveryFields = [
            'deliveryName', 'deliveryLastname', 'deliveryAddress', 
            'deliveryZip', 'deliveryCity'
        ];
        
        deliveryFields.forEach(fieldName => {
            const element = this.elements[fieldName];
            if (element) {
                element.addEventListener('input', () => {
                    element.dataset.touched = 'true';
                    this.handleFormChange();
                });
                element.addEventListener('blur', () => {
                    element.dataset.touched = 'true';
                    this.validateField(fieldName);
                });
            }
        });

        // Add to cart button
        if (this.elements.addToCartButton) {
            this.elements.addToCartButton.addEventListener('click', () => this.handleAddToCart());
        }
    }

    /**
     * Setup variant price listener for products with variants
     */
    setupVariantPriceListener() {
        // Listen for variant selection changes from variants.js
        document.addEventListener('variantSelected', (event) => {
            const variant = event.detail.variant;
            if (variant && variant.price) {
                this.selectedAmount = this.getVariantPurchasePrice(variant);
                this.selectedVariant = variant; // Remember variant so its id reaches the order row
                this.updateFaceValueDisplay(variant);
                console.log('GiftCardFormManager: Variant selected, amount updated to:', this.selectedAmount, 'from variant:', variant.name);
                this.updateButtonState();
            }
        });
        
        // CRITICAL FIX: Check if there's already a selected variant (timing fix)
        // This handles the case where selectDefaultVariant() runs before this listener is registered
        setTimeout(() => {
            // Access the global variant manager (exposed as window.productVariantManager or window.ecommerce.variants)
            const variantManager = window.productVariantManager || window.ecommerce?.variants;
            if (variantManager && variantManager.currentVariant) {
                const variant = variantManager.currentVariant;
                if (variant && variant.price) {
                    this.selectedAmount = this.getVariantPurchasePrice(variant);
                    this.selectedVariant = variant; // Remember variant so its id reaches the order row
                    this.updateFaceValueDisplay(variant);
                    console.log('GiftCardFormManager: Initial variant detected, amount set to:', this.selectedAmount, 'from variant:', variant.name);
                    this.updateButtonState();
                }
            }
        }, 100); // Small delay to ensure variant manager has initialized

        console.log('GiftCardFormManager: Variant price listener setup complete');
    }

    /**
     * Get the price the customer pays for a variant: the discount price when a
     * valid one exists, otherwise the base price. The card's face value
     * (BasePrice) is handled server-side at minting - it is never read here.
     */
    getVariantPurchasePrice(variant) {
        return (variant.hasDiscountPrice && variant.discountPrice)
            ? variant.discountPrice
            : variant.price;
    }

    /**
     * Update the "Presentkortsvärde" row on variant gift card products so the
     * displayed face value always matches the selected variant.
     */
    updateFaceValueDisplay(variant) {
        const element = getElement('.js-giftcard-face-value');
        if (element && variant && variant.formattedPrice) {
            element.textContent = variant.formattedPrice;
        }
    }

    /**
     * Setup custom amount input handling
     */
    setupCustomAmountInput() {
        const customInput = this.elements.customAmount;
        if (!customInput) return;
        
        customInput.addEventListener('input', () => {
            this.handleCustomAmountChange();
        });
        
        customInput.addEventListener('blur', () => {
            this.validateCustomAmount();
        });
        
        console.log('Custom amount input initialized');
    }

    /**
     * Setup quick select buttons
     */
    setupQuickSelectButtons() {
        const buttons = document.querySelectorAll('.js-amount-quick');
        if (buttons.length === 0) return;
        
        buttons.forEach(button => {
            button.addEventListener('click', () => {
                this.handleQuickSelect(button);
            });
        });
        
        console.log(`${buttons.length} quick select buttons initialized`);
    }

    /**
     * Handle quick select button click
     */
    handleQuickSelect(button) {
        const amount = parseInt(button.dataset.amount);
        
        // Update selected button state
        document.querySelectorAll('.js-amount-quick').forEach(btn => {
            btn.classList.remove('selected');
        });
        button.classList.add('selected');
        
        // Update input field
        if (this.elements.customAmount) {
            this.elements.customAmount.value = amount;
        }
        
        // Set selected amount
        this.selectedAmount = amount;
        this.updateAmountDisplay();
        this.handleFormChange();
        
        console.log('Quick select amount:', amount);
    }

    /**
     * Handle custom amount input change
     */
    handleCustomAmountChange() {
        const value = this.elements.customAmount?.value;
        
        // Clear quick select buttons
        document.querySelectorAll('.js-amount-quick').forEach(btn => {
            btn.classList.remove('selected');
        });
        
        if (value && !isNaN(value)) {
            const amount = parseFloat(value);
            this.selectedAmount = amount;
            this.updateAmountDisplay();
        } else {
            this.selectedAmount = null;
            this.updateAmountDisplay();
        }
        
        this.handleFormChange();
    }

    /**
     * Validate custom amount
     */
    validateCustomAmount() {
        const customInput = this.elements.customAmount;
        const errorElement = this.elements.errorCustomAmount;
        
        if (!customInput || !errorElement) return true;
        
        const value = customInput.value.trim();
        
        if (!value) {
            errorElement.textContent = t('giftCard.customAmount.validation.required');
            errorElement.style.display = 'block';
            customInput.classList.add('error');
            return false;
        }
        
        const amount = parseFloat(value);
        const minAmount = this.giftCardConfig.minAmount || 100;
        const maxAmount = this.giftCardConfig.maxAmount || 10000;
        
        if (isNaN(amount)) {
            errorElement.textContent = t('giftCard.customAmount.validation.invalid');
            errorElement.style.display = 'block';
            customInput.classList.add('error');
            return false;
        }
        
        if (amount < minAmount) {
            errorElement.textContent = t('giftCard.customAmount.validation.tooLow')
                .replace('{min}', minAmount);
            errorElement.style.display = 'block';
            customInput.classList.add('error');
            return false;
        }
        
        if (amount > maxAmount) {
            errorElement.textContent = t('giftCard.customAmount.validation.tooHigh')
                .replace('{max}', maxAmount);
            errorElement.style.display = 'block';
            customInput.classList.add('error');
            return false;
        }
        
        // Valid amount
        errorElement.style.display = 'none';
        customInput.classList.remove('error');
        return true;
    }

    /**
     * Update amount display
     */
    updateAmountDisplay() {
        if (!this.elements.selectedAmountDisplay) return;

        if (this.selectedAmount && this.selectedAmount > 0) {
            this.elements.selectedAmountDisplay.textContent = `${this.selectedAmount.toLocaleString('sv-SE')} ${t('giftCard.customAmount.currencySuffix')}`;
            this.elements.selectedAmountDisplay.style.color = '#28a745';
        } else {
            this.elements.selectedAmountDisplay.textContent = t('giftCard.customAmount.noneSelected');
            this.elements.selectedAmountDisplay.style.color = '#6c757d';
        }
    }

    /**
     * Handle form field changes
     */
    handleFormChange() {
        this.collectFormData();
        this.updateSummary();
        this.updateButtonState();
    }

    /**
     * Collect form data
     */
    collectFormData() {
        this.formData = {
            recipientName: this.elements.recipientName?.value.trim() || '',
            recipientEmail: this.elements.recipientEmail?.value.trim() || '',
            giftMessage: this.elements.giftMessage?.value.trim() || '',
            senderName: this.elements.senderName?.value.trim() || ''
        };
    }

    /**
     * Setup physical card option visibility and initial state
     */
    setupPhysicalCardOption() {
        // Check if physical cards are allowed for this customer
        const allowPhysicalCard = window.allowPhysicalGiftCard || false;
        const physicalCardPrice = window.physicalGiftCardPrice || 75;
        
        console.log('Physical card configuration:', { allowPhysicalCard, physicalCardPrice });
        
        if (allowPhysicalCard && this.elements.giftCardTypeGroup) {
            // Show the gift card type selection group
            this.elements.giftCardTypeGroup.style.display = 'block';
            
            // Update the price display in the radiobutton label
            if (this.elements.physicalCardPriceDisplay) {
                this.elements.physicalCardPriceDisplay.textContent = physicalCardPrice;
            }
            
            console.log('Gift card type selection enabled and visible');
        } else {
            // Hide the gift card type selection if not allowed
            if (this.elements.giftCardTypeGroup) {
                this.elements.giftCardTypeGroup.style.display = 'none';
            }
            console.log('Physical card option disabled - only digital cards available');
        }
    }

    /**
     * Shows or hides recipient fields based on the requireRecipientFields setting.
     * When false (e.g. punch cards / klippkort), recipient name, email and message
     * are irrelevant and are hidden from the form. The add-to-cart button becomes
     * enabled as soon as a valid amount is selected.
     */
    applyRecipientFieldsVisibility() {
        if (this.requireRecipientFields) {
            return; // Default behavior: all fields visible and required
        }

        // Hide recipient name field
        const recipientNameGroup = this.elements.recipientName?.closest('.form-group');
        if (recipientNameGroup) recipientNameGroup.style.display = 'none';

        // Hide recipient email group
        if (this.elements.recipientEmailGroup) {
            this.elements.recipientEmailGroup.style.display = 'none';
        }

        // Hide gift message field
        const giftMessageGroup = this.elements.giftMessage?.closest('.form-group');
        if (giftMessageGroup) giftMessageGroup.style.display = 'none';
        // Also hide the char counter that follows the textarea
        const charCounter = document.querySelector('.char-counter');
        if (charCounter) charCounter.style.display = 'none';

        // Hide sender name field
        const senderNameGroup = this.elements.senderName?.closest('.form-group');
        if (senderNameGroup) senderNameGroup.style.display = 'none';

        // Hide physical card type selector (not relevant for punch cards)
        if (this.elements.giftCardTypeGroup) {
            this.elements.giftCardTypeGroup.style.display = 'none';
        }

        console.log('GiftCardFormManager: Recipient fields hidden (requireRecipientFields=false)');
    }

    /**
     * Handle gift card type change (radiobuttons)
     */
    handleGiftCardTypeChange() {
        const isPhysicalCard = this.elements.giftCardTypePhysical?.checked || false;
        
        // Show/hide email section - only show for digital cards
        if (this.elements.recipientEmailGroup) {
            this.elements.recipientEmailGroup.style.display = isPhysicalCard ? 'none' : 'block';
        }
        
        // Show/hide delivery address section - only show for physical cards
        if (this.elements.physicalDeliverySection) {
            this.elements.physicalDeliverySection.style.display = isPhysicalCard ? 'block' : 'none';
        }
        
        // Clear email field when switching to physical card
        if (isPhysicalCard && this.elements.recipientEmail) {
            this.elements.recipientEmail.value = '';
            this.elements.recipientEmail.dataset.touched = 'false';
            // Clear any email validation errors
            const formGroup = this.elements.recipientEmail.closest('.form-group');
            if (formGroup) {
                formGroup.classList.remove('error');
            }
            if (this.elements.errorRecipientEmail) {
                this.elements.errorRecipientEmail.style.display = 'none';
            }
        }
        
        // Update form validation and state
        this.handleFormChange();
        
        console.log('Gift card type changed:', isPhysicalCard ? 'Physical' : 'Digital');
    }

    /**
     * Update physical card price display
     */
    updatePhysicalCardPriceDisplay() {
        if (!this.elements.physicalCardPrice) return;
        
        const isPhysicalCard = this.elements.physicalCard?.checked || false;
        const physicalCardPrice = window.physicalGiftCardPrice || 75;
        
        if (isPhysicalCard) {
            this.elements.physicalCardPrice.textContent = `+${physicalCardPrice} kr`;
            this.elements.physicalCardPrice.style.display = 'inline';
        } else {
            this.elements.physicalCardPrice.style.display = 'none';
        }
    }

    /**
     * Get total price including physical card surcharge
     */
    getTotalPrice() {
        let totalPrice = this.selectedAmount || 0;
        
        // Add physical card surcharge if selected
        if (this.elements.physicalCard?.checked && window.physicalGiftCardPrice) {
            totalPrice += window.physicalGiftCardPrice;
        }
        
        return totalPrice;
    }

    /**
     * Update character count for message field
     */
    updateCharCount() {
        if (!this.elements.charCount || !this.elements.giftMessage) return;

        const count = this.elements.giftMessage.value.length;
        this.elements.charCount.textContent = count;
    }

    /**
     * Update summary display
     */
    updateSummary() {
        if (this.elements.summaryRecipient) {
            this.elements.summaryRecipient.textContent = this.formData.recipientName || '-';
        }
        
        if (this.elements.summarySender) {
            this.elements.summarySender.textContent = this.formData.senderName || '-';
        }
    }

    /**
     * Validate individual field
     */
    validateField(fieldName) {
        const field = this.elements[fieldName];
        const errorElement = this.elements[`error${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}`];
        
        if (!field || !errorElement) return true;

        let isValid = true;
        let errorMessage = '';

        const value = field.value.trim();

        // Only validate if field has been touched (has content or was focused)
        if (!value && !field.dataset.touched) {
            return true; // Don't validate empty untouched fields
        }

        switch (fieldName) {
            case 'recipientName':
                // Skip validation if recipient fields are not required (punch cards etc.)
                if (!this.requireRecipientFields) return true;
                if (!value) {
                    isValid = false;
                    errorMessage = t('giftCard.validation.recipientNameRequired');
                } else if (value.length < 2) {
                    isValid = false;
                    errorMessage = t('giftCard.validation.nameTooShort');
                }
                break;

            case 'recipientEmail':
                // Skip validation if recipient fields are not required (punch cards etc.)
                if (!this.requireRecipientFields) return true;
                // Only validate email for digital cards
                if (!this.isPhysicalCardSelected()) {
                    if (!value) {
                        isValid = false;
                        errorMessage = t('giftCard.validation.emailRequired');
                    } else if (!this.isValidEmail(value)) {
                        isValid = false;
                        errorMessage = t('giftCard.validation.emailInvalid');
                    }
                }
                break;

            case 'senderName':
                // Skip validation if recipient fields are not required (punch cards etc.)
                if (!this.requireRecipientFields) return true;
                if (!value) {
                    isValid = false;
                    errorMessage = t('giftCard.validation.senderNameRequired');
                } else if (value.length < 2) {
                    isValid = false;
                    errorMessage = t('giftCard.validation.nameTooShort');
                }
                break;

            // Delivery address validation (only required if physical card is selected)
            case 'deliveryName':
                if (this.isPhysicalCardSelected() && !value) {
                    isValid = false;
                    errorMessage = t('giftCard.validation.deliveryNameRequired');
                } else if (value && value.length < 2) {
                    isValid = false;
                    errorMessage = t('giftCard.validation.nameTooShort');
                }
                break;

            case 'deliveryLastname':
                if (this.isPhysicalCardSelected() && !value) {
                    isValid = false;
                    errorMessage = t('giftCard.validation.deliveryLastnameRequired');
                } else if (value && value.length < 2) {
                    isValid = false;
                    errorMessage = t('giftCard.validation.nameTooShort');
                }
                break;

            case 'deliveryAddress':
                if (this.isPhysicalCardSelected() && !value) {
                    isValid = false;
                    errorMessage = t('giftCard.validation.deliveryAddressRequired');
                } else if (value && value.length < 5) {
                    isValid = false;
                    errorMessage = t('giftCard.validation.addressTooShort');
                }
                break;

            case 'deliveryZip':
                if (this.isPhysicalCardSelected() && !value) {
                    isValid = false;
                    errorMessage = t('giftCard.validation.deliveryZipRequired');
                } else if (value && !/^\d{5}$/.test(value)) {
                    isValid = false;
                    errorMessage = t('giftCard.validation.zipInvalid');
                }
                break;

            case 'deliveryCity':
                if (this.isPhysicalCardSelected() && !value) {
                    isValid = false;
                    errorMessage = t('giftCard.validation.deliveryCityRequired');
                } else if (value && value.length < 2) {
                    isValid = false;
                    errorMessage = t('giftCard.validation.cityTooShort');
                }
                break;
        }

        // Update field styling and error message
        const formGroup = field.closest('.form-group');
        if (formGroup) {
            if (isValid) {
                formGroup.classList.remove('error');
                errorElement.style.display = 'none';
            } else {
                formGroup.classList.add('error');
                errorElement.textContent = errorMessage;
                errorElement.style.display = 'block';
            }
        }

        return isValid;
    }

    /**
     * Validate email format
     */
    isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    /**
     * Update form validation and button state
     */
    updateFormValidation() {
        // Validate all required fields
        const recipientNameValid = this.validateField('recipientName');
        const recipientEmailValid = this.validateField('recipientEmail');
        const senderNameValid = this.validateField('senderName');
        const amountValid = this.selectedAmount && this.selectedAmount > 0;

        // Validate delivery address fields if physical card is selected
        let deliveryValid = true;
        if (this.isPhysicalCardSelected()) {
            const deliveryNameValid = this.validateField('deliveryName');
            const deliveryLastnameValid = this.validateField('deliveryLastname');
            const deliveryAddressValid = this.validateField('deliveryAddress');
            const deliveryZipValid = this.validateField('deliveryZip');
            const deliveryCityValid = this.validateField('deliveryCity');
            
            deliveryValid = deliveryNameValid && deliveryLastnameValid && 
                           deliveryAddressValid && deliveryZipValid && deliveryCityValid;
        }

        this.isValid = recipientNameValid && recipientEmailValid && senderNameValid && 
                      amountValid && deliveryValid;

        this.updateButtonState();
    }

    /**
     * Update button state and validation summary
     */
    updateButtonState() {
        const hasAmount = this.selectedAmount && this.selectedAmount > 0;

        // When requireRecipientFields is false (punch cards / klippkort):
        // only a valid amount is needed to enable the button
        if (!this.requireRecipientFields) {
            this.isValid = !!hasAmount;
            if (this.elements.addToCartButton) {
                this.elements.addToCartButton.disabled = !hasAmount;
            }
            if (this.elements.validationSummary) {
                this.elements.validationSummary.style.display = (!hasAmount && this.hasAttemptedSubmit) ? 'block' : 'none';
            }
            return;
        }

        // Standard gift card: check all recipient fields
        const hasRecipientName = this.formData.recipientName && this.formData.recipientName.length >= 2;
        // Email is only required for digital cards
        const hasValidEmail = this.isPhysicalCardSelected() || 
                             (this.formData.recipientEmail && this.isValidEmail(this.formData.recipientEmail));
        const hasSenderName = this.formData.senderName && this.formData.senderName.length >= 2;

        // Check delivery address if physical card is selected
        let hasValidDelivery = true;
        if (this.isPhysicalCardSelected()) {
            const deliveryData = this.getDeliveryAddressData();
            hasValidDelivery = deliveryData.deliveryName && deliveryData.deliveryName.length >= 2 &&
                              deliveryData.deliveryLastname && deliveryData.deliveryLastname.length >= 2 &&
                              deliveryData.deliveryAddress && deliveryData.deliveryAddress.length >= 5 &&
                              deliveryData.deliveryZip && /^\d{5}$/.test(deliveryData.deliveryZip) &&
                              deliveryData.deliveryCity && deliveryData.deliveryCity.length >= 2;
        }

        const isComplete = hasRecipientName && hasValidEmail && hasSenderName && hasAmount && hasValidDelivery;

        // Update the isValid flag that handleAddToCart() checks
        this.isValid = isComplete;

        // Update button state
        if (this.elements.addToCartButton) {
            this.elements.addToCartButton.disabled = !isComplete;
        }

        // Update validation summary - only show if user has actually tried to submit
        if (this.elements.validationSummary) {
            this.elements.validationSummary.style.display = (!isComplete && this.hasAttemptedSubmit) ? 'block' : 'none';
        }

        // Debug logging (reduce frequency to avoid spam)
        if (Math.random() < 0.1) { // Only log 10% of the time
            console.log('GiftCardFormManager: Form validation state:', {
                hasRecipientName,
                hasValidEmail,
                hasSenderName,
                hasAmount,
                hasValidDelivery,
                isPhysicalCard: this.isPhysicalCardSelected(),
                isComplete,
                selectedAmount: this.selectedAmount,
                formData: this.formData
            });
        }
    }

    /**
     * Check if user has interacted with the form
     */
    hasUserInteracted() {
        return (this.elements.recipientName?.dataset.touched === 'true') ||
               (this.elements.recipientEmail?.dataset.touched === 'true') ||
               (this.elements.senderName?.dataset.touched === 'true') ||
               (this.selectedAmount !== null);
    }

    /**
     * Handle add to cart action
     */
    handleAddToCart() {
        // Set the flag that user has attempted to submit
        this.hasAttemptedSubmit = true;
        
        // CRITICAL: Validate custom amount before proceeding
        const amountValid = this.validateCustomAmount();
        if (!amountValid) {
            console.warn('Cannot add to cart: invalid amount');
            this.updateButtonState();
            return;
        }
        
        // Update button state to show validation messages if needed
        this.updateButtonState();
        
        if (!this.isValid) {
            console.warn('Cannot add to cart: form is not valid');
            return;
        }

        try {
            // Collect all data
            this.collectFormData();

            // Check if physical card is selected
            const isPhysicalCard = this.isPhysicalCardSelected();

            // Prepare cart item data
            const cartItemData = {
                productId: this.getProductId(),
                variantId: this.selectedVariant?.id || null, // Set for variant-based gift cards, null for custom/fixed amounts
                quantity: 1,
                price: this.selectedAmount, // Use ORIGINAL amount only (not including physical card surcharge)
                giftCardData: {
                    recipientName: this.formData.recipientName,
                    // Only include email for digital cards
                    recipientEmail: isPhysicalCard ? '' : this.formData.recipientEmail,
                    giftMessage: this.formData.giftMessage,
                    senderName: this.formData.senderName,
                    amount: this.selectedAmount, // Original gift card amount
                    isPhysicalCard: isPhysicalCard,
                    physicalCardPrice: isPhysicalCard ? (window.physicalGiftCardPrice || 75) : 0,
                    // Add delivery address if physical card is selected
                    ...(isPhysicalCard && { deliveryAddress: this.getDeliveryAddressData() })
                }
            };

            // Add to cart
            this.addToCart(cartItemData);

            // Show success feedback
            this.showSuccessFeedback();

        } catch (error) {
            console.error('Failed to add gift card to cart:', error);
            this.showErrorFeedback(t('giftCard.errors.addToCartFailed'));
        }
    }

    /**
     * Get product ID from page data
     */
    getProductId() {
        // Try to get from cart elements
        const cartIdElement = getElement('.js-cart-id');
        if (cartIdElement) {
            return cartIdElement.textContent.trim();
        }
        
        // Fallback: try to extract from URL or other sources
        const urlParts = window.location.pathname.split('/');
        const productIdIndex = urlParts.indexOf('product') + 1;
        if (productIdIndex > 0 && productIdIndex < urlParts.length) {
            return urlParts[productIdIndex];
        }
        
        throw new Error(t('giftCard.errors.noProductId'));
    }

    /**
     * Get the product GUID from page data.
     * Unlike ProductNr (a human-readable string) this identifier cannot be
     * corrupted by browser translation plugins.
     */
    getProductGuid() {
        const productIdElement = getElement('.js-cart-productid');
        return productIdElement ? productIdElement.textContent.trim() : null;
    }

    /**
     * Add item to cart
     * Creates separate order rows for gift card and physical card surcharge
     */
    addToCart(cartItemData) {
        // Resolve the configured storage key. The rest of the system uses
        // config.storageKey (tenant-unique, cartItems_<merchantId>, set by the
        // published ecommerce include) - a hardcoded 'cartItems' here would
        // write the gift card row to a key the checkout never reads.
        const storageKey = window.ecommerce?.config?.storageKey
            || window.ecommerceConfig?.storageKey
            || 'cartItems';

        // Get existing cart
        let cart = getStorage(storageKey) || [];

        // Create gift card order row (always with original amount)
        const giftCardItem = {
            // Standard cart properties
            ProductName: this.getProductName(),
            ProductNr: cartItemData.productId,
            ProductId: this.getProductGuid(), // Translation-proof identifier (GUID survives DOM translation)
            ProductVariantId: cartItemData.variantId || null, // Variant id when a variant was selected - required so the card mints at the variant's face value
            ProductPrice: this.selectedAmount.toFixed(2), // Original gift card amount only
            TaxRate: this.getTaxRate().toString(),
            ProductUrl: this.getProductUrl(),
            ImageUrl: this.getProductImage(),
            OrderCount: cartItemData.quantity,
            Type: 'GiftCard', // Explicit type for gift card
            
            // Gift card specific properties
            isGiftCard: true,
            giftCardData: cartItemData.giftCardData
        };

        // Add gift card to cart
        cart.push(giftCardItem);
        console.log('Gift card added to cart:', giftCardItem);

        // If physical card is selected, add separate surcharge order row
        if (cartItemData.giftCardData.isPhysicalCard && 
            cartItemData.giftCardData.physicalCardPrice > 0) {
            
            const surchargeItem = {
                ProductName: 'Fysiskt presentkort',
                ProductNr: 'PHYSICAL-GIFTCARD',
                ProductPrice: cartItemData.giftCardData.physicalCardPrice.toFixed(2),
                TaxRate: this.getTaxRate().toString(),
                ProductUrl: '',
                ImageUrl: '',
                OrderCount: 1,
                Type: 'Surcharge' // Explicit type for surcharge
            };
            
            cart.push(surchargeItem);
            console.log('Physical card surcharge added to cart:', surchargeItem);
        }
        
        // Save cart
        setStorage(storageKey, cart);

        // Dispatch cart updated event
        dispatchCartUpdatedEvent(cart);

        // Open the inline cart so the customer can review the added gift card
        if (window.ecommerce && window.ecommerce.inlineCart) {
            window.ecommerce.inlineCart.open(this.elements.addToCartButton);
        }
    }

    /**
     * Get product name from page
     */
    getProductName() {
        const nameElement = getElement('.js-cart-name');
        return nameElement ? nameElement.textContent.trim() : t('giftCard.defaultName');
    }

    /**
     * Get product image from page
     */
    getProductImage() {
        const imageElement = getElement('.js-cart-image');
        return imageElement ? imageElement.src : '';
    }

    /**
     * Get product URL from page
     */
    getProductUrl() {
        const urlElement = getElement('.js-cart-url');
        return urlElement ? urlElement.textContent.trim() : window.location.href;
    }

    /**
     * Get tax rate from page
     */
    getTaxRate() {
        const taxRateElement = getElement('.js-cart-taxrate');
        const taxRate = taxRateElement ? parseFloat(taxRateElement.textContent) || 25 : 25;
        // Return as number for internal calculations (will be converted to string in addToCart)
        return Number(taxRate);
    }

    /**
     * Show success feedback
     */
    showSuccessFeedback() {
        // Temporarily change button text
        const originalText = this.elements.addToCartButton.textContent;
        this.elements.addToCartButton.textContent = t('giftCard.addedToCart');
        this.elements.addToCartButton.classList.add('success');
        
        setTimeout(() => {
            this.elements.addToCartButton.textContent = originalText;
            this.elements.addToCartButton.classList.remove('success');
        }, 2000);
    }


    /**
     * Check if physical card is selected
     */
    isPhysicalCardSelected() {
        return this.elements.giftCardTypePhysical?.checked || false;
    }
    /**
     * Show error feedback
     */
    showErrorFeedback(message) {
        if (this.elements.validationSummary) {
            const errorText = this.elements.validationSummary.querySelector('.error-text');
            if (errorText) {
                errorText.textContent = message;
            }
            this.elements.validationSummary.style.display = 'block';
        }
    }

    /**
     * Get delivery address data
     */
    getDeliveryAddressData() {
        return {
            deliveryName: this.elements.deliveryName?.value.trim() || '',
            deliveryLastname: this.elements.deliveryLastname?.value.trim() || '',
            deliveryAddress: this.elements.deliveryAddress?.value.trim() || '',
            deliveryAddress2: this.elements.deliveryAddress2?.value.trim() || '',
            deliveryZip: this.elements.deliveryZip?.value.trim() || '',
            deliveryCity: this.elements.deliveryCity?.value.trim() || '',
            deliveryCountry: this.elements.deliveryCountry?.value || 'SE'
        };
    }
}

// Auto-initialize if gift card product is detected
document.addEventListener('DOMContentLoaded', () => {
    if (window.isGiftCardProduct) {
        window.giftCardFormManager = new GiftCardFormManager();
    }
});

// Export for manual initialization
export default GiftCardFormManager;
